import type { ContentFitnessDimension } from "contracts/report/content-fitness/dimensions";
import { healthState } from "contracts/report/health";
import type { ContentFitnessOrigin } from "../../enums/scan";
import { CONTENT_FITNESS_ORIGIN } from "../../enums/scan";
import { healthLabel, healthVar } from "../../ui/health";
import { CLIENT_TERMS, criteriaCoverageLabel } from "./clientLanguage";
import {
  coverageWord,
  type DimensionAssessment,
  dimensionCoverageStatus,
} from "./contentFitnessDimensionModel";
import { Fact } from "./Fact";
import { ScanBreadcrumbs } from "./ScanRouteChrome";
import type { PillarSignal } from "./scanModel";

export function ScorePanel(props: { signal: PillarSignal | null }) {
  const score = () => props.signal?.score ?? null;
  const state = () => props.signal?.state ?? healthState(null, 80, 60);
  return (
    <div class="scan-metric scan-dimension-score">
      <span>Dimension score</span>
      <strong class="mono" style={{ color: healthVar(state()) }}>
        {score() ?? "--"}
      </strong>
      <small class={`sig-state st-${state()}`}>{healthLabel(state())}</small>
    </div>
  );
}

export function EmptyState(props: { loading: boolean; text: string }) {
  return (
    <p class="signal-muted">
      {props.loading ? "Loading page assessment data..." : props.text}
    </p>
  );
}

export function DimensionSummaryPanels(props: {
  dimension: ContentFitnessDimension;
  pageCoverage: {
    capturedPages: number;
    assessedPages: number;
    scoredPages: number;
  };
  signal: PillarSignal | null;
  assessments: DimensionAssessment[];
  pilotCount: number;
  canonicalCount: number;
}) {
  return (
    <div class="scan-grid-two scan-detail-grid">
      <section class="scan-panel">
        <span class="scan-kicker">Dimension summary</span>
        <dl class="scan-facts">
          <Fact
            label="Weight"
            value={`${props.dimension.weight}% of Content Fitness`}
          />
          <Fact
            label="Pages in inventory"
            value={`${props.pageCoverage.capturedPages}`}
          />
          <Fact
            label="Pages assessed"
            value={`${props.pageCoverage.assessedPages} of ${props.pageCoverage.capturedPages} inventory pages`}
          />
          <Fact
            label="Pages scored"
            value={`${props.pageCoverage.scoredPages} of ${props.pageCoverage.capturedPages} inventory pages`}
          />
          <Fact
            label={CLIENT_TERMS.assessmentCoverage}
            value={dimensionCoverageStatus(
              props.signal,
              props.assessments,
              props.dimension.key,
            )}
          />
        </dl>
      </section>

      <section class="scan-panel">
        <span class="scan-kicker">{CLIENT_TERMS.assessmentCoverage}</span>
        <div class="scan-coverage-callout">
          <strong>
            {criteriaCoverageLabel(
              props.signal?.coverage?.assessedSubcriteria ?? 0,
              props.canonicalCount,
            )}
          </strong>
          <span>{coverageWord(props.signal)}</span>
          <p>
            This scan assesses {props.pilotCount} criteria for this dimension.
            Criteria without usable evidence are not inferred or treated as
            zero.
          </p>
        </div>
      </section>
    </div>
  );
}

export function DimensionTechnicalDetail(props: {
  dimension: ContentFitnessDimension;
  pilotCount: number;
  canonicalCount: number;
}) {
  return (
    <details class="scan-disclosure scan-technical-detail">
      <summary>Technical detail</summary>
      <div class="scan-disclosure-body">
        <p class="scan-muted">
          Framework identifiers and configuration details used to reproduce this
          dimension view.
        </p>
        <dl class="scan-facts">
          <Fact
            label="Canonical dimension key"
            value={props.dimension.key}
            mono
          />
          <Fact label="Weight" value={`${props.dimension.weight}%`} />
          <Fact label="Criteria assessed" value={`${props.pilotCount}`} />
          <Fact
            label="Criteria in the framework"
            value={`${props.canonicalCount}`}
          />
        </dl>
      </div>
    </details>
  );
}

export function UnknownDimension(props: {
  websiteId: string;
  dimensionKey: string;
  origin: ContentFitnessOrigin;
}) {
  const parent = () =>
    props.origin === CONTENT_FITNESS_ORIGIN.SCORECARD
      ? {
          label: "Scorecard",
          href: `/w/${props.websiteId}/scan/signals`,
        }
      : { label: "Summary", href: `/w/${props.websiteId}/scan` };
  return (
    <>
      <ScanBreadcrumbs
        items={[
          { label: "Signal Scan", href: `/w/${props.websiteId}/scan` },
          parent(),
          { label: "Unknown dimension" },
        ]}
      />
      <section class="scan-panel">
        <span class="scan-kicker">Content Fitness v1.2</span>
        <h1 class="scan-title">Unknown dimension</h1>
        <p class="scan-lede">
          This dimension is not available in the active Content Fitness
          framework.
        </p>
      </section>
    </>
  );
}
